import React, { useEffect, useRef, useState } from "react";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import type { ResearchDossierCaptureV2 } from "@shared/researchDossier";
import ResearchDossierDisplay, { type DossierVersionSummary } from "@/components/ResearchDossierDisplay";

type DossierResponse = {
  dossier: { id: string; currentReportId: string; questionId: string };
  report: { id: string; title?: string };
  capture: ResearchDossierCaptureV2;
  manifestDigest: string;
  versions: DossierVersionSummary[];
};
type ReassessRequest = { expectedParentReportId: string; requestId: string; dossierId: string; selectedReportId: string };

function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  throw new Error("This browser cannot create a secure request ID. Update the browser and try again.");
}

async function bodyOf(response: Response) {
  try { return await response.json() as Record<string, unknown>; }
  catch { return {}; }
}

const ResearchDossierReport: React.FC<{ reportId: string; onUpdated: (reportId: string) => void }> = ({ reportId, onUpdated }) => {
  const reportIdRef = useRef(reportId);
  reportIdRef.current = reportId;
  const [data, setData] = useState<DossierResponse | null>(null);
  const [notDossier, setNotDossier] = useState(false);
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<ReassessRequest | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setNotDossier(false);
    setError(null);
    setBusy(false);
    setPending(null);
    fetch(`/api/research-dossiers/reports/${encodeURIComponent(reportId)}`, {
      headers: getClientIdentityHeaders(), credentials: "include", cache: "no-store", signal: controller.signal,
    }).then(async (response) => {
      const payload = await bodyOf(response);
      if (controller.signal.aborted) return;
      if (response.status === 404) {
        setNotDossier(true);
        return;
      }
      if (!response.ok) {
        setError({ message: `Saved dossier could not be read (HTTP ${response.status})${typeof payload.detail === "string" ? `: ${payload.detail}` : "."}`, retryable: false });
        return;
      }
      if (!payload.capture || !payload.dossier || !Array.isArray(payload.versions)) {
        setError({ message: "The saved dossier response is incomplete.", retryable: false });
        return;
      }
      setData(payload as unknown as DossierResponse);
    }).catch((failure) => {
      if (!controller.signal.aborted) setError({ message: failure instanceof Error ? `Saved dossier could not be read: ${failure.message}` : "Saved dossier could not be read.", retryable: false });
    });
    return () => controller.abort();
  }, [reportId]);

  const sendReassessment = async (request: ReassessRequest) => {
    if (!data || request.selectedReportId !== reportIdRef.current) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`/api/research-dossiers/${encodeURIComponent(request.dossierId)}/reassess`, {
        method: "POST",
        credentials: "include",
        headers: { ...getClientIdentityHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ expectedParentReportId: request.expectedParentReportId, requestId: request.requestId }),
      });
      const payload = await bodyOf(response);
      if (request.selectedReportId !== reportIdRef.current) return;
      if (!response.ok) {
        const detail = typeof payload.detail === "string" ? `: ${payload.detail}` : ".";
        setError({ message: `Manual reassessment failed with HTTP ${response.status}${detail}`, retryable: response.status >= 500 });
        return;
      }
      if (!payload.report || typeof payload.report !== "object" || typeof (payload.report as { id?: unknown }).id !== "string") {
        setError({ message: "The server response is incomplete. The reassessment may have been saved; retry with the same request ID.", retryable: true });
        return;
      }
      const nextReportId = (payload.report as { id: string }).id;
      setPending(null);
      onUpdated(nextReportId);
    } catch {
      if (request.selectedReportId === reportIdRef.current) setError({ message: "No HTTP response was received. The reassessment result is unknown.", retryable: true });
    } finally {
      if (request.selectedReportId === reportIdRef.current) setBusy(false);
    }
  };

  const handleReassess = () => {
    if (!data || busy || reportId !== data.dossier.currentReportId) return;
    try {
      const request = { expectedParentReportId: reportId, requestId: uuid(), dossierId: data.dossier.id, selectedReportId: reportId };
      setPending(request);
      void sendReassessment(request);
    } catch (failure) {
      setError({ message: failure instanceof Error ? failure.message : "Could not create a secure request ID.", retryable: false });
    }
  };

  if (notDossier) return null;
  if (!data && !error) return <section className="reports-detail-evidence" aria-live="polite">Loading saved dossier…</section>;

  return <section aria-label="Saved question dossier">
    {error && <div className="analytics-error" role="alert" aria-live="assertive">
      <p>{error.message}</p>
      {error.retryable && pending && <button type="button" className="analytics-action-btn" onClick={() => void sendReassessment(pending)} disabled={busy}>Retry the same reassessment</button>}
    </div>}
    {data && <>
      <div className="reports-detail-evidence">
        <h3>Manual reassessment</h3>
        <p>A new assessment runs only when you select this button. It uses the saved dossier definition and compares with the current saved version.</p>
        {reportId !== data.dossier.currentReportId && <p>This is an older saved version. Open the latest version before reassessing: <a href={`/reports?id=${encodeURIComponent(data.dossier.currentReportId)}`}>Open latest version</a>.</p>}
        <button type="button" className="analytics-action-btn" onClick={handleReassess} disabled={busy || reportId !== data.dossier.currentReportId || (!!pending && error?.retryable === true)}>
          {busy ? "Saving reassessment…" : "Reassess now"}
        </button>
      </div>
      <ResearchDossierDisplay capture={data.capture} versions={data.versions} />
    </>}
  </section>;
};

export default ResearchDossierReport;
